import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { FaGithub, FaLinkedin } from 'react-icons/fa';
import { HiArrowDown } from 'react-icons/hi';
import { VideoBackground } from './VideoBackground';

/* ── Mouse parallax hook ── */
function useMouseParallax(strength = 0.02) {
  const [pos, setPos] = useState({ x: 0, y: 0 });
  useEffect(() => {
    const handler = (e) => {
      const cx = window.innerWidth / 2;
      const cy = window.innerHeight / 2;
      setPos({
        x: (e.clientX - cx) * strength,
        y: (e.clientY - cy) * strength,
      });
    };
    window.addEventListener('mousemove', handler, { passive: true });
    return () => window.removeEventListener('mousemove', handler);
  }, [strength]);
  return pos;
}

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 28 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] },
});

export default function Hero() {
  const mouse = useMouseParallax(0.015);

  return (
    <section
      id="home"
      className="relative min-h-screen flex items-center overflow-hidden"
      style={{ paddingTop: '100px', paddingBottom: '60px' }}
    >
      {/* ── VIDEO BACKGROUND WITH MOUSE SCRUB ── */}
      <VideoBackground />

      {/* ── AMBIENT ORBS ── */}
      <div
        className="absolute pointer-events-none"
        style={{
          top: '-10%', left: '-10%', width: '60%', height: '60%',
          background: 'radial-gradient(circle, rgba(0,242,255,0.06) 0%, transparent 70%)',
          filter: 'blur(40px)',
          transform: `translate(${mouse.x * 1.5}px, ${mouse.y * 1.5}px)`,
          transition: 'transform 0.1s linear',
        }}
      />
      <div
        className="absolute pointer-events-none"
        style={{
          bottom: '-10%', right: '-5%', width: '50%', height: '50%',
          background: 'radial-gradient(circle, rgba(139,92,246,0.07) 0%, transparent 70%)',
          filter: 'blur(40px)',
          transform: `translate(${-mouse.x}px, ${-mouse.y}px)`,
          transition: 'transform 0.1s linear',
        }}
      />

      <div className="container-custom w-full" style={{ position: 'relative', zIndex: 10 }}>
        <div className="min-h-[calc(100vh-160px)] flex items-center">

          {/* ═══════════════════════════════════
              CONTENT COLUMN — LEFT ALIGNED
          ═══════════════════════════════════ */}
          <div className="w-full max-w-3xl">
            {/* Glassmorphism content wrapper for readability on video background */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              className="card p-5 sm:p-8 lg:p-10 flex flex-col justify-center w-full"
              style={{
                background: 'rgba(13, 20, 29, 0.8)',
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: '24px',
                boxShadow: '0 20px 60px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.05)',
              }}
            >

            {/* Status badge */}
            <motion.div {...fadeUp(0.1)} className="mb-5 sm:mb-7">
              <span className="status-badge text-[0.68rem] sm:text-xs leading-relaxed max-w-full flex-wrap sm:flex-nowrap">
                <span className="status-dot shrink-0" />
                <span>Software Engineer · Full Stack</span>
              </span>
            </motion.div>

            {/* Headline */}
            <motion.h1 {...fadeUp(0.25)} className="heading-display mb-4 sm:mb-6 leading-[1.15] sm:leading-[1.05]">
              <span className="text-gradient">Full Stack Developer</span>
              {' '}&{' '}
              <span style={{ color: '#00f2ff' }}>Software Engineer</span>
            </motion.h1>

            {/* Description */}
            <motion.p
              {...fadeUp(0.4)}
              className="text-sm sm:text-base lg:text-lg mb-6 sm:mb-10 leading-relaxed"
              style={{
                color: '#b9cacb',
                maxWidth: '520px',
              }}
            >
              Full Stack Developer &amp; Software Engineer specializing in{' '}
              <span style={{ color: '#dce3f0', fontWeight: 500 }}>React</span>,{' '}
              <span style={{ color: '#dce3f0', fontWeight: 500 }}>.NET</span>,{' '}
              <span style={{ color: '#dce3f0', fontWeight: 500 }}>Node.js</span>, cloud and AI-powered applications.
              Currently crafting enterprise-scale systems at{' '}
              <a
                href="https://www.cognizant.com"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#00f2ff', textDecoration: 'none', borderBottom: '1px solid rgba(0,242,255,0.3)' }}
              >
                Cognizant
              </a>.
            </motion.p>

            {/* CTAs */}
            <motion.div {...fadeUp(0.52)} className="flex flex-wrap sm:flex-nowrap items-center gap-3 mb-8 sm:mb-10">
              <a
                href="#projects"
                className="btn-primary group w-full xs:w-auto justify-center"
                onClick={(e) => { e.preventDefault(); document.getElementById('projects')?.scrollIntoView({ behavior: 'smooth' }); }}
                style={{ padding: '0.65rem 1.25rem', fontSize: '0.85rem', whiteSpace: 'nowrap' }}
              >
                View My Work
                <HiArrowDown
                  size={14}
                  className="transition-transform duration-300 group-hover:translate-y-0.5"
                />
              </a>
              <a
                href="https://drive.google.com/file/d/1PRcA0JQRkY-xUub4g6b5-_PmDAQS-vUl/view?usp=sharing"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary w-full xs:w-auto justify-center"
                style={{ padding: '0.65rem 1.25rem', fontSize: '0.85rem', whiteSpace: 'nowrap' }}
              >
                Download CV
              </a>
            </motion.div>

            {/* Social links */}
            <motion.div {...fadeUp(0.62)} className="flex flex-wrap items-center gap-3 sm:gap-5">
              <a
                href="https://github.com/atmanad"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#849495', transition: 'color 0.2s', display: 'flex', alignItems: 'center' }}
                onMouseEnter={(e) => (e.currentTarget.style.color = '#00f2ff')}
                onMouseLeave={(e) => (e.currentTarget.style.color = '#849495')}
                aria-label="GitHub"
              >
                <FaGithub size={22} />
              </a>
              <a
                href="https://www.linkedin.com/in/atmanad/"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#849495', transition: 'color 0.2s', display: 'flex', alignItems: 'center' }}
                onMouseEnter={(e) => (e.currentTarget.style.color = '#00f2ff')}
                onMouseLeave={(e) => (e.currentTarget.style.color = '#849495')}
                aria-label="LinkedIn"
              >
                <FaLinkedin size={22} />
              </a>
              <span className="hidden xs:inline-block" style={{ width: '1px', height: '20px', background: 'rgba(255,255,255,0.1)' }} />
              <span
                className="text-mono truncate"
                style={{ color: '#849495', fontSize: '0.75rem', letterSpacing: '0.05em' }}
              >
                atmanad0001@gmail.com
              </span>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </div>

      {/* ── Scroll indicator ── */}
      <motion.a
        href="#about"
        onClick={(e) => { e.preventDefault(); document.getElementById('about')?.scrollIntoView({ behavior: 'smooth' }); }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden sm:flex flex-col items-center gap-2"
        style={{ textDecoration: 'none' }}
      >
        <span className="text-mono" style={{ color: '#849495', fontSize: '0.65rem', letterSpacing: '0.1em' }}>
          SCROLL
        </span>
        <motion.div
          animate={{ y: [0, 6, 0] }}
          transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
        >
          <HiArrowDown size={18} color="#849495" />
        </motion.div>
      </motion.a>
    </section>
  );
}
